import { requestLogin } from '@/lib/auth/login-dialog';
/**
 * Session 认证层
 * 使用 authStore（自签 JWT）管理认证。
 */
import { useAuthStore, ensureValidToken } from '@/stores/authStore';
import { requestAuthScopedStateReset } from '@/lib/auth-scoped-state-reset';
import { API_BASE_URL, resolveCookieDomainAttribute } from '@/lib/api/base-url';
import { buildSignedOutLoginPath, SIGNED_OUT_LOGIN_PATH } from '@/lib/auth/login-route';
import {
  assertFreshClientBeforeCriticalAction,
  getClientBuildHeaders,
  isCriticalClientMutation,
} from '@/lib/version/client-version';

/** 兼容类型：最小 Session 子集 */
interface SessionCompat {
  access_token: string;
  user: { id: string; email?: string };
}

function isBackendTransportUrl(url: string): boolean {
  if (url.startsWith('/')) {
    return true;
  }

  try {
    return new URL(url).origin === new URL(API_BASE_URL).origin;
  } catch {
    return false;
  }
}

/**
 * 获取当前 Session
 * 返回包含 access_token 和 user 的对象，或 null
 */
export async function getSessionSafe(): Promise<SessionCompat | null> {
  const token = await ensureValidToken();
  if (!token) return null;

  const { user } = useAuthStore.getState();
  return {
    access_token: token,
    user: { id: user?.id ?? '', email: user?.email },
  };
}

/**
 * 获取 Access Token
 */
export async function getAccessToken(forceRefresh = false): Promise<string | null> {
  return ensureValidToken(forceRefresh);
}

/**
 * 创建带认证的 fetch headers
 */
export async function createAuthHeaders(contentType?: string): Promise<HeadersInit> {
  const token = await ensureValidToken();
  const headers: HeadersInit = {
    ...getClientBuildHeaders(),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (contentType) {
    headers['Content-Type'] = contentType;
  }
  return headers;
}

/**
 * 带认证的 fetch 封装
 * 自动添加 Authorization header，支持超时和重试
 */
export class AuthFetchTimeoutError extends Error {
  constructor(message = 'Request timed out') {
    super(message);
    this.name = 'TimeoutError';
  }
}

export class AuthExpiredError extends Error {
  redirectTo: string;

  constructor(redirectTo: string, message = 'Authentication expired') {
    super(message);
    this.name = 'AuthExpiredError';
    this.redirectTo = redirectTo;
  }
}

export function buildLoginRedirectPath(pathname?: string, search?: string): string {
  if (typeof window === 'undefined') {
    return SIGNED_OUT_LOGIN_PATH;
  }

  const currentPathname = pathname ?? window.location.pathname;
  const currentSearch = search ?? window.location.search;
  const current = `${currentPathname || '/'}${currentSearch || ''}`;
  if (!currentPathname || currentPathname.startsWith('/login')) {
    return SIGNED_OUT_LOGIN_PATH;
  }

  return buildSignedOutLoginPath(current);
}

function clearLoggedInCookieFallback(): void {
  if (typeof window === 'undefined') return;
  const secure = window.location.protocol === 'https:';
  const domainPart = resolveCookieDomainAttribute();
  document.cookie = `logged_in=; path=/; max-age=0; samesite=lax${secure ? '; secure' : ''}`;
  document.cookie = `logged_in=; path=/; max-age=0; samesite=lax${secure ? '; secure' : ''}${domainPart}`;
}

export async function clearClientAuthState(): Promise<void> {
  if (typeof window === 'undefined') return;

  try {
    await useAuthStore.getState().logout();
  } catch {
    useAuthStore.setState({
      user: null,
      accessToken: null,
      tokenExpiresAt: 0,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
    clearLoggedInCookieFallback();
    window.localStorage.removeItem('octo-auth');
    window.localStorage.removeItem('lepus-auth');
    requestAuthScopedStateReset();
  }
}

export async function handleAuthExpiredRedirect(options: { redirect?: boolean } = {}): Promise<never> {
  const redirect = options.redirect ?? (typeof window === 'undefined' || window.location.pathname !== '/kagemane');
  const loginPath = buildLoginRedirectPath();
  await clearClientAuthState();

  if (redirect && typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    requestLogin(loginPath);
  }

  throw new AuthExpiredError(loginPath);
}

export async function authFetch(
  url: string,
  options: RequestInit & { timeout?: number; retries?: number } = {}
): Promise<Response> {
  const { timeout = 180000, retries: explicitRetries, ...fetchOptions } = options;
  const { signal: externalSignal, ...requestOptions } = fetchOptions;
  const method = (fetchOptions.method || 'GET').toUpperCase();
  const retries = explicitRetries ?? (method === 'GET' || method === 'HEAD' || method === 'OPTIONS' ? 2 : 0);

  if (isCriticalClientMutation(method)) {
    await assertFreshClientBeforeCriticalAction();
  }

  let lastError: Error | null = null;
  let token = await ensureValidToken();
  let authRetried = false;
  let hadAuthenticatedContext = Boolean(token) || useAuthStore.getState().isAuthenticated;

  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    let didTimeout = false;
    const abortFromExternalSignal = () => controller.abort();
    if (externalSignal?.aborted) {
      controller.abort();
    } else {
      externalSignal?.addEventListener('abort', abortFromExternalSignal, { once: true });
    }
    const timeoutId = setTimeout(() => {
      didTimeout = true;
      controller.abort();
    }, timeout);
    const headers = new Headers(fetchOptions.headers);
    Object.entries(getClientBuildHeaders()).forEach(([key, value]) => {
      headers.set(key, value);
    });
    // Declare the surface only to our own backend; never copy the entitlement
    // routing signal onto a third-party upload or media URL.
    if (isBackendTransportUrl(url)) {
      headers.set('X-Client-Surface', 'web');
    }
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    try {
      const response = await fetch(url, {
        ...requestOptions,
        headers,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      externalSignal?.removeEventListener('abort', abortFromExternalSignal);

      if (response.status === 401 && !authRetried) {
        authRetried = true;
        token = await ensureValidToken(true);
        if (token) {
          hadAuthenticatedContext = true;
          continue;
        }
      }

      if (response.status === 403 && !authRetried && hadAuthenticatedContext && !token) {
        authRetried = true;
        token = await ensureValidToken(true);
        if (token) {
          hadAuthenticatedContext = true;
          continue;
        }
      }

      if (
        (response.status === 401 || response.status === 403)
        && hadAuthenticatedContext
        && !useAuthStore.getState().isAuthenticated
      ) {
        await handleAuthExpiredRedirect();
      }

      const isRetryableServerError = response.status >= 500 && response.status <= 504;
      if (isRetryableServerError && attempt < retries) {
        const retryDelay = 500 * Math.pow(2, attempt);
        console.warn(`[authFetch] ${method} ${url} 返回 ${response.status}，${retryDelay}ms 后重试 (${attempt + 1}/${retries})`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        continue;
      }

      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      externalSignal?.removeEventListener('abort', abortFromExternalSignal);
      if (didTimeout && (error as Error).name === 'AbortError' && !externalSignal?.aborted) {
        error = new AuthFetchTimeoutError();
      }
      lastError = error as Error;
      const isExternalAbort = externalSignal?.aborted && (error as Error).name === 'AbortError';

      const isRetryableError =
        !isExternalAbort && (
          error instanceof TypeError ||
          error instanceof AuthFetchTimeoutError ||
          (error as Error).name === 'AbortError' ||
          (error as Error).message?.includes('socket') ||
          (error as Error).message?.includes('network') ||
          (error as Error).message?.includes('ECONNRESET')
        );

      if (isRetryableError && attempt < retries) {
        const delay = 500 * Math.pow(2, attempt);
        console.warn(`[authFetch] ${method} ${url} 请求失败，${delay}ms 后重试 (${attempt + 1}/${retries}):`, (error as Error).message);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      throw error;
    }
  }

  throw lastError;
}
